import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Logo />
            </div>
            <p className="mt-4 text-gray-400 max-w-md">
              {t('footer.explore.description')}
            </p>
            <div className="flex mt-6 space-x-4">
              <a 
                href="https://www.facebook.com/TravelCord.guide" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/travelcord.guide/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.explore.title')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/travel-guides" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('footer.explore.findGuides')}
                </Link>
              </li>
              <li>
                <Link to="/uganda-trips" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('footer.explore.safariAdventures')}
                </Link>
              </li>
              <li>
                <Link to="/all-experiences" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('footer.explore.culturalExperiences')}
                </Link>
              </li>
              <li>
                <Link to="/uganda-trips" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('footer.explore.wildlifeTours')}
                </Link>
              </li>
              <li>
                <Link to="/uganda-city-tours" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('footer.explore.cityExplorations')}
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Free Travel Resources
                </Link>
              </li>
              <li>
                <Link to="/Uganda_Nightlife_Ultimate_Guide" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center">
                  <Moon className="h-3 w-3 mr-1" />
                  Uganda Nightlife Guide
                  <span className="ml-1 bg-ta-green text-white text-[8px] px-1 py-0.5 rounded-full">NEW</span>
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.about.title')}</h3>
            <ul className="space-y-2">
              <li><Link to="/about-us" className="text-gray-400 hover:text-white transition-colors text-sm">{t('footer.about.aboutUs')}</Link></li>
              <li>
                <button 
                  onClick={() => document.getElementById('become-guide-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors text-sm text-left"
                >
                  {t('footer.about.becomeGuide')}
                </button>
              </li>
              <li>
                <Link to="/partnership" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('footer.about.partnership')}
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('footer.about.careers')}
                </Link>
              </li>
              <li>
                <Link to="/customize-adventure" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Customize your safari
                </Link>
              </li>
              <li>
                <Link to="/write-for-us" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Write for Us
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.contact.title')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 text-ta-green mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm">{t('footer.contact.address')}</span>
              </li>
              <li className="flex items-center">
                <Phone className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                <span className="text-gray-400 text-sm">{t('footer.contact.phone')}</span>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                <span className="text-gray-400 text-sm">{t('footer.contact.email')}</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              {t('footer.legal.copyright')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-4 md:mt-0">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors text-sm">{t('footer.legal.privacy')}</Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors text-sm">{t('footer.legal.terms')}</Link>
              <Link to="/cookie-policy" className="text-gray-400 hover:text-white transition-colors text-sm">{t('footer.legal.cookies')}</Link>
              <Link to="/sitemap" className="text-gray-400 hover:text-white transition-colors text-sm">{t('footer.legal.sitemap')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;